import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import './AdminAPITokens.css';

function AdminAPITokens() {
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [newTokenValue, setNewTokenValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rate_limit: 60,
    allowed_ips: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchTokens();
    fetchStats();
  }, [filterActive, searchTerm]);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const params = {};

      if (filterActive !== 'all') {
        params.is_active = filterActive === 'active' ? 'true' : 'false';
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await api.get('/api/admin/api-tokens', { params });
      setTokens(response.data.tokens);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      toast.error('Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/admin/api-tokens/stats');
      console.log('Admin stats API response:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default stats to prevent undefined errors
      setStats({
        total: 0,
        active: 0,
        total_usage: 0,
        recently_used: 0
      });
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Token name is required');
      return;
    }

    try {
      const response = await api.post('/api/admin/api-tokens', formData);

      // Show the token value (only shown once!)
      setNewTokenValue(response.data.token.token);
      setShowTokenModal(true);
      setShowCreateModal(false);

      // Reset form
      setFormData({
        name: '',
        description: '',
        rate_limit: 60,
        allowed_ips: '',
        expires_at: ''
      });

      fetchTokens();
      fetchStats();
      toast.success('API token created successfully');
    } catch (error) {
      console.error('Error creating token:', error);
      toast.error(error.response?.data?.error || 'Failed to create API token');
    }
  };

  const handleUpdateToken = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/admin/api-tokens/${selectedToken.id}`, formData);

      setShowEditModal(false);
      setSelectedToken(null);

      fetchTokens();
      toast.success('API token updated successfully');
    } catch (error) {
      console.error('Error updating token:', error);
      toast.error(error.response?.data?.error || 'Failed to update API token');
    }
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this API token? This action cannot be undone.')) {
      return;
    }

    try {
      await api.post(`/admin/api-tokens/${tokenId}/revoke`);
      fetchTokens();
      fetchStats();
      toast.success('API token revoked successfully');
    } catch (error) {
      console.error('Error revoking token:', error);
      toast.error(error.response?.data?.error || 'Failed to revoke API token');
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to permanently delete this API token? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/api-tokens/${tokenId}`);
      fetchTokens();
      fetchStats();
      toast.success('API token deleted successfully');
    } catch (error) {
      console.error('Error deleting token:', error);
      toast.error(error.response?.data?.error || 'Failed to delete API token');
    }
  };

  const openEditModal = (token) => {
    setSelectedToken(token);
    setFormData({
      name: token.name,
      description: token.description || '',
      rate_limit: token.rate_limit,
      allowed_ips: token.allowed_ips ? token.allowed_ips.join(', ') : '',
      expires_at: token.expires_at || ''
    });
    setShowEditModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Token copied to clipboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container fluid className="admin-api-tokens-page">
      <Row className="mb-4">
        <Col>
          <h1>API Token Management</h1>
          <p className="text-muted">Manage API tokens for external integrations and third-party access</p>
        </Col>
      </Row>

      {/* Statistics */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <h6 className="text-muted">Total Tokens</h6>
                <h2>{stats?.total ?? 0}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <h6 className="text-muted">Active Tokens</h6>
                <h2 className="text-success">{stats?.active ?? 0}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <h6 className="text-muted">Total API Calls</h6>
                <h2>{(stats?.total_usage ?? 0).toLocaleString()}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <h6 className="text-muted">Recently Used (24h)</h6>
                <h2>{stats?.recently_used ?? 0}</h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Actions and Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="all">All Tokens</option>
            <option value="active">Active Only</option>
            <option value="inactive">Revoked Only</option>
          </Form.Select>
        </Col>
        <Col md={5} className="text-end">
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Create New API Token
          </Button>
        </Col>
      </Row>

      {/* Tokens Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No API tokens found</p>
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                Create Your First API Token
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Token Prefix</th>
                  <th>Description</th>
                  <th>Rate Limit</th>
                  <th>Usage</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id}>
                    <td>
                      <strong>{token.name}</strong>
                      <br />
                      <small className="text-muted">
                        Created by {token.created_by?.name}
                      </small>
                    </td>
                    <td>
                      <code>{token.token_prefix}...</code>
                    </td>
                    <td>
                      {token.description ? (
                        <small>{token.description}</small>
                      ) : (
                        <em className="text-muted">No description</em>
                      )}
                    </td>
                    <td>{token.rate_limit}/min</td>
                    <td>{token.usage_count.toLocaleString()} calls</td>
                    <td>
                      <small>{formatDate(token.last_used_at)}</small>
                    </td>
                    <td>
                      {token.is_active ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="danger">Revoked</Badge>
                      )}
                      {token.expires_at && new Date(token.expires_at) < new Date() && (
                        <Badge bg="warning" className="ms-1">Expired</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openEditModal(token)}
                      >
                        Edit
                      </Button>
                      {token.is_active ? (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          onClick={() => handleRevokeToken(token.id)}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteToken(token.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Token Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New API Token</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateToken}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Token Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Production API, Mobile App"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                A friendly name to identify this token
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What is this token used for?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rate Limit (requests per minute)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="1000"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
              />
              <Form.Text className="text-muted">
                Maximum number of API requests allowed per minute (default: 60)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Allowed IP Addresses (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 192.168.1.1, 10.0.0.1"
                value={formData.allowed_ips}
                onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
              />
              <Form.Text className="text-muted">
                Comma-separated list of IP addresses. Leave empty to allow all IPs.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiration Date (optional)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
              <Form.Text className="text-muted">
                Leave empty for tokens that never expire
              </Form.Text>
            </Form.Group>

            <Alert variant="warning">
              <strong>Important:</strong> The token will only be displayed once after creation. Make sure to save it securely!
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Token
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Token Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit API Token</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateToken}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Token Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rate Limit (requests per minute)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="1000"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Allowed IP Addresses</Form.Label>
              <Form.Control
                type="text"
                value={formData.allowed_ips}
                onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiration Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Token
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Show Token Modal (only shown once after creation) */}
      <Modal show={showTokenModal} onHide={() => setShowTokenModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>API Token Created Successfully</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>⚠️ Important: Save this token now!</strong><br />
            For security reasons, this token will only be displayed once. You won't be able to see it again.
          </Alert>

          <Form.Group>
            <Form.Label><strong>Your API Token:</strong></Form.Label>
            <div className="d-flex">
              <Form.Control
                type="text"
                value={newTokenValue}
                readOnly
                className="font-monospace"
              />
              <Button
                variant="outline-primary"
                className="ms-2"
                onClick={() => copyToClipboard(newTokenValue)}
              >
                Copy
              </Button>
            </div>
          </Form.Group>

          <Alert variant="info" className="mt-3">
            <strong>How to use this token:</strong>
            <pre className="mb-0 mt-2">
{`curl -H "X-API-Token: ${newTokenValue}" \\
     https://your-domain.com/api/v1/properties`}
            </pre>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTokenModal(false)}>
            I've Saved the Token
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminAPITokens;
